import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Team Leader FTL (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'ftlLead',
  title: 'Team Leader FTL',
  audience:
    'Dla Team Leadera FTL (w systemie rola „Planista główny FTL (master)”) i administratorów, którzy podejmują decyzje o obsadzeniu floty.',
  intro:
    'Team Leader FTL robi wszystko to, co planista (rozdział „Planista FTL”), a do tego podejmuje decyzje: obsadza zlecenia pojazdem, rozpatruje wnioski planistów, przekazuje obsadzone zlecenia do Trade i reaguje na alerty o przeplanowaniu. Ten rozdział opisuje tylko te dodatkowe czynności.',
  sections: [
    {
      id: 'rola',
      title: 'Twoja rola i uprawnienia',
      blocks: [
        {
          kind: 'text',
          text: 'Planista FTL przygotowuje decyzję: przegląda rekomendacje i dodaje warianty, czyli pojazdy, które rozważa dla zlecenia. Team Leader wybiera spośród wariantów i obsadza zlecenie. Obsadzenie to finalne przypisanie pojazdu — od tej chwili zlecenie ma stan „Obsadzone flotą”, a pojazd widać w harmonogramie floty pełnym paskiem.',
        },
        {
          kind: 'list',
          title: 'Co możesz jako Team Leader (poza pracą planisty)',
          items: [
            'obsadzić zlecenie pojazdem i zmienić pojazd w obsadzonym zleceniu,',
            'odpiąć pojazd od obsadzonego zlecenia albo cofnąć zlecenie do „Wstępnie planowane”,',
            'przekazać do Trade zlecenie, które jest już obsadzone pojazdem floty,',
            'zaakceptować albo odrzucić wnioski planistów (o odpięcie, a przy włączonych funkcjach — o zmianę pojazdu i o przekazanie do Trade),',
            'pracować z wariantami wszystkich pojazdów, nie tylko „swoich”.',
          ],
        },
        {
          kind: 'terms',
          title: 'Stany zlecenia, które zmieniasz najczęściej',
          items: [
            { term: 'Gotowe do planowania', meaning: 'Zlecenie czeka na pojazd, nie ma jeszcze wariantów.' },
            { term: 'Wstępnie planowane', meaning: 'Zlecenie ma co najmniej jeden wariant — planiści wskazali pojazdy, decyzja należy do Ciebie.' },
            { term: 'Obsadzone flotą', meaning: 'Zlecenie ma przypisany pojazd floty własnej. Pozostałe warianty zostały zwolnione.' },
            { term: 'Przekazane do Trade', meaning: 'Zlecenie prowadzi dział Trade (przewoźnicy, giełdy) — szczegóły w rozdziale „Trade”.' },
          ],
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'System przygotowuje, człowiek zatwierdza. Rekomendacje, ostrzeżenia i alerty to podpowiedzi — obsadzenie, odpięcie i zmianę pojazdu zawsze wykonuje człowiek. Obsadzenie w aplikacji niczego nie zapisuje w TMS (VIA): do TMS trafia wyłącznie plik i tylko po jawnej akcji człowieka.',
        },
        {
          kind: 'text',
          when: { on: ['assignTeamLeaderOnly'] },
          text: 'Zleceń nie obsadza konto z rolą „Użytkownik”: obsadzają wyłącznie Team Leader i administratorzy. „Użytkownik” dalej dodaje warianty, pracuje w Trade i przyjmuje zlecenia, ale przyciski obsadzenia są dla niego niedostępne.',
        },
        {
          kind: 'text',
          when: { off: ['assignTeamLeaderOnly'] },
          text: 'Obsadzać mogą Team Leader, administratorzy i konta z rolą „Użytkownik”. Planista FTL obsadzać nie może — wskazuje pojazd wariantem.',
        },
      ],
    },
    {
      id: 'pulpit-team-leadera',
      title: 'Pulpit Team Leadera',
      menu: ['ftl-team-leader-dashboard'],
      when: { on: ['teamLeaderDashboard'] },
      blocks: [
        {
          kind: 'text',
          text: 'Pozycja menu „Pulpit Team Leadera” zbiera w jednym miejscu to, co czeka na Twoją decyzję. Każda liczba otwiera listę zleceń z tym samym filtrem, którym ją policzono, więc liczba i lista zawsze się zgadzają. Ekranem startowym po zalogowaniu zostaje pulpit ogólny.',
        },
        {
          kind: 'image',
          image: { guide: 'teamLeaderDashboard', step: 1 },
          caption: 'Pulpit Team Leadera — kafelki „Do obsadzenia z wariantami” i „Do obsadzenia bez wariantu”.',
        },
        {
          kind: 'terms',
          title: 'Kafelki pulpitu',
          items: [
            { term: 'Do obsadzenia z wariantami', meaning: 'Planiści wskazali pojazdy — zlecenie czeka na Twoją decyzję.' },
            { term: 'Do obsadzenia bez wariantu', meaning: 'Żaden planista nie wskazał jeszcze pojazdu.' },
            { term: 'Konflikty wariantów', meaning: 'Ten sam pojazd jest wariantem kilku zleceń w tym samym czasie.' },
            { term: 'Warianty po terminie', meaning: 'Wariant, którego termin ważności już minął.', when: { on: ['variantValidity'] } },
            { term: 'Wnioski o odpięcie', meaning: 'Wnioski planistów czekające na Twoją decyzję.' },
            {
              term: 'Wnioski o zmianę pojazdu i przekazanie',
              meaning: 'Wnioski planistów złożone po wyjeździe pojazdu na załadunek.',
              when: { on: ['vehicleChangeRequest'] },
            },
            { term: 'Straciły pojazd', meaning: 'Zlecenia, które były obsadzone, a straciły pojazd i wróciły do planowania.' },
            {
              term: 'Transporeon — brak w TMS',
              meaning: 'Transporty przydzielone w Transporeonie, których po godzinie nie ma w TMS — liczba Twojej spółki (konto bez spółki: suma z rozbiciem na spółki). Kliknięcie otwiera listę tylko do odczytu.',
              when: { on: ['transporeonImport'] },
            },
            { term: 'Jutro załadunek, bez auta', meaning: 'Zlecenia z załadunkiem jutro, które nadal nie mają pojazdu.', when: { on: ['loadingTomorrowAlert'] } },
            { term: 'Alerty przeplanowania', meaning: 'Obsadzone zlecenia, przy których system podpowiada przeplanowanie.' },
            { term: 'W FTL / W Trade / Przekazane dziś do Trade', meaning: 'Bilans: ile zleceń prowadzi flota, ile Trade i ile przekazano dziś.' },
          ],
        },
        {
          kind: 'task',
          title: 'Zacznij dzień od pulpitu',
          steps: [
            'Otwórz „Pulpit Team Leadera” w menu.',
            'Kliknij kafelek „Wnioski o odpięcie” (i „Wnioski o zmianę pojazdu i przekazanie”, jeśli jest) — rozstrzygnij wnioski na karcie zlecenia.',
            'Kliknij „Straciły pojazd” (i „Jutro załadunek, bez auta”, jeśli jest) — te zlecenia są najpilniejsze.',
            'Kliknij „Do obsadzenia z wariantami” i obsadź zlecenia, przy których planiści wskazali pojazdy.',
          ],
          outcome: 'Po każdej decyzji wróć na pulpit — liczby przeliczą się przy odświeżeniu.',
        },
        {
          kind: 'text',
          when: { on: ['teamLeaderBalance'] },
          text: 'Pod kafelkami są dwie tabele. „Bilans floty i postęp planistów” — wiersz na każdego opiekuna FTL, „bez opiekuna” i cała flota: pojazdy, wolne teraz i za 6, 12 i 24 godziny, pojazdy z planem sięgającym poza jutro i zlecenia z wariantem czekającym na decyzję. „Deficyt i nadwyżka według kraju załadunku” — zlecenia bez pojazdu z załadunkiem w horyzoncie wobec pojazdów, które w tym czasie zwalniają się w tym kraju. Liczby to szacunek; kliknięcie liczby otwiera harmonogram floty albo listę zleceń.',
        },
        {
          kind: 'image',
          when: { on: ['teamLeaderBalance'] },
          image: { guide: 'teamLeaderBalance', step: 1 },
          caption: 'Bilans floty i postęp planistów na pulpicie Team Leadera.',
        },
      ],
    },
    {
      id: 'obsadzanie',
      title: 'Obsadzanie zleceń',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenie obsadzasz z listy rekomendowanych pojazdów albo z wariantu na karcie zlecenia (gdy jest taka możliwość — także z wiersza listy zleceń). Każda z tych dróg to ta sama operacja: pojazd zostaje przypisany, pozostałe warianty zlecenia są zwalniane, a kandydatury tego pojazdu przy innych zleceniach w tym samym czasie także — każde dotknięte zlecenie dostaje wpis w historii zmian.',
        },
        {
          kind: 'task',
          title: 'Obsadzić zlecenie z rekomendacji',
          intro: 'Gdy chcesz wybrać pojazd z pełnej listy kandydatów, z dolotem i ostrzeżeniami.',
          steps: [
            'Otwórz kartę zlecenia z listy zleceń.',
            'Kliknij „Pokaż rekomendowane pojazdy”.',
            'Przejrzyj kandydatów: kolejność to dolot, a obok stoją „zdąży”, „dowiezie”, zapas czasu pracy, koszt i uwagi. Rozwiń „Na jakiej podstawie”, jeśli chcesz sprawdzić wyliczenie.',
            'Przy wybranym pojeździe kliknij „Obsadź”.',
          ],
          outcome: 'Zlecenie przechodzi do „Obsadzone flotą”, a pojazd pojawia się w harmonogramie floty. Gdy pojazdu nie było wśród wariantów, system zakłada wariant „z rekomendacji”.',
        },
        {
          kind: 'task',
          title: 'Obsadzić zlecenie wariantem wskazanym przez planistę',
          steps: [
            'Otwórz kartę zlecenia w stanie „Wstępnie planowane”.',
            'W sekcji „Akcje” wybierz pojazd w polu „Obsadź pojazdem” (lista zawiera warianty z ich miejscem w rekomendacjach).',
            'Kliknij „Obsadź”.',
          ],
          outcome: 'Pozostałe warianty zostaną zwolnione — ich autorzy zobaczą to w historii zlecenia.',
        },
        {
          kind: 'task',
          title: 'Obsadzić z listy zleceń, bez otwierania karty',
          when: { on: ['bankRowAssign'] },
          steps: [
            'Na liście zleceń kliknij „Obsadź…” w wierszu zlecenia.',
            'Porównaj warianty w oknie — wyliczenia są takie jak w rekomendacjach.',
            'Kliknij „Obsadź tym pojazdem” przy wybranym wariancie.',
          ],
        },
        {
          kind: 'task',
          title: 'Zmienić pojazd w obsadzonym zleceniu',
          intro: 'Zmiana pojazdu w ramach własnej floty nie wymaga niczyjej zgody.',
          steps: [
            'Dodaj nowy pojazd jako wariant („Sugerowane pojazdy” → „Dodaj wariant”) albo przyciskiem „Wariant” w rekomendacjach.',
            'W polu „Zmień obsadzenie na” wybierz ten pojazd.',
            'Opcjonalnie wpisz „Powód zmiany pojazdu” — trafi do historii zmian zlecenia.',
            'Kliknij „Zmień pojazd”.',
          ],
        },
        {
          kind: 'task',
          title: 'Obsadzić mimo braku czasu',
          intro: 'Gdy wybrany pojazd według wyliczeń nie zmieści się w oknach zlecenia.',
          steps: [
            'Wybierz pojazd w „Obsadź pojazdem” — pod polem pojawi się ostrzeżenie o czasie.',
            'Kliknij „Obsadź” i przeczytaj ostrzeżenie w oknie „Obsadzić mimo braku czasu?”.',
            'Kliknij „Obsadź mimo braku czasu” albo „Anuluj”.',
          ],
          outcome: 'To ostrzeżenie, nie blokada — czas jazdy to szacunek. Decyzja zostaje w historii zmian.',
        },
        {
          kind: 'terms',
          title: 'Co blokuje obsadzenie',
          items: [
            { term: 'Uprawnienie typu Blokada', meaning: 'Nieważne w dniu załadunku uprawnienie pojazdu albo kierowcy (np. przegląd, OC, tachograf).' },
            { term: 'Za mała ładowność', meaning: 'Znana ładowność zestawu jest mniejsza niż masa towaru. Ładowność domyślna (gdy kartoteka jej nie ma) tylko ostrzega.' },
            { term: 'ADR „nie”', meaning: 'Zlecenie z ADR, a pojazd albo kierowca ma wpisane „nie”. Brak wpisu tylko ostrzega.' },
            { term: 'Serwis w realizacji', meaning: 'Pojazd ma w TMS zlecenie serwisowe „W realizacji”.' },
            { term: 'Poza eksploatacją', meaning: 'Pojazd oznaczony w TMS jako poza eksploatacją.' },
            {
              term: 'Niedostępny w TMS',
              meaning: 'Pojazd oznaczony w TMS jako niedostępny w dniu załadunku. Odmowa podaje powód i termin z TMS; obejść tego nie może nikt.',
              when: { on: ['unavailableBlocksAssignment'] },
            },
          ],
        },
        {
          kind: 'text',
          when: { off: ['unavailableBlocksAssignment'] },
          text: 'Niedostępność pojazdu z TMS nie blokuje obsadzenia ręcznego — rekomendacje i tak pomijają taki pojazd z powodem, ale z wariantu da się go obsadzić.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Czas pracy kierowcy, typ pojazdu, wózek, kabotaż i spółka pojazdu nie blokują obsadzenia — to ostrzeżenia. Przeczytaj je przed decyzją: odpowiedzialność za wybór jest po Twojej stronie.',
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['recommendationSnapshot'] },
          text: 'Przy obsadzeniu i dodaniu wariantu historia zlecenia zapisuje, co lista rekomendacji mówiła o tym pojeździe w chwili decyzji: miejsce na liście, dolot, „zdąży”, „dowiezie”, uwagi i trzy pierwsze pozycje listy. Gdy listy nie liczono, wpis mówi to wprost.',
        },
      ],
    },
    {
      id: 'obsadzenie-mimo-blokady',
      title: 'Obsadzenie mimo blokady',
      blocks: [
        {
          kind: 'text',
          when: { off: ['assignDespiteBlock'] },
          text: 'Blokada obsadzenia blokuje każdego, także Team Leadera. Zamiast obsadzenia pojawia się „Obsadzenie niedostępne:” z powodem. Wybierz inny pojazd albo poproś o uzupełnienie danych w TMS.',
        },
        {
          kind: 'text',
          when: { on: ['assignDespiteBlock'] },
          text: 'Gdy obsadzenie blokuje wyłącznie nieważne uprawnienie pojazdu albo kierowcy typu Blokada, Team Leader i administratorzy mogą obsadzić mimo blokady — z powodem.',
        },
        {
          kind: 'text',
          when: { on: ['assignDespiteBlock'], off: ['assignOverrideWider'] },
          text: 'Za mała ładowność, ADR „nie”, pojazd poza eksploatacją i pojazd niedostępny w TMS blokują dalej każdego.',
        },
        {
          kind: 'text',
          when: { on: ['assignDespiteBlock', 'assignOverrideWider'] },
          text: 'Obejść możesz także za małą znaną ładowność zestawu i pojazd oznaczony w TMS jako poza eksploatacją. Bez obejścia dla nikogo zostają: nieważne zezwolenie na pracę kierowcy, pojazd niedostępny w TMS i ADR „nie”.',
        },
        {
          kind: 'task',
          title: 'Obsadzić mimo blokady',
          when: { on: ['assignDespiteBlock'] },
          steps: [
            'Wybierz pojazd w „Obsadź pojazdem” (albo w rekomendacjach) i kliknij „Obsadź”.',
            'Zamiast odmowy otworzy się okno „Obsadź mimo blokady” z listą blokad — przeczytaj ją.',
            'Wpisz „Powód obsadzenia mimo blokady” (co najmniej 10 znaków).',
            'Kliknij „Obsadź mimo blokady”.',
          ],
          outcome: 'Historia zmian zlecenia zapisuje, kto obsadził, dlaczego i które blokady obszedł.',
        },
        {
          kind: 'image',
          when: { on: ['assignDespiteBlock', 'assignOverrideWider'] },
          image: { guide: 'assignOverrideWider', step: 1 },
          caption: 'Okno „Obsadź mimo blokady” z listą blokad i polem powodu.',
        },
        {
          kind: 'note',
          tone: 'warning',
          when: { on: ['assignDespiteBlock'] },
          text: 'Obejście blokady to Twoja świadoma decyzja. Sprawdź u planisty albo w TMS, czy dokument faktycznie wygasł, zanim pojazd wyjedzie.',
        },
      ],
    },
    {
      id: 'wnioski',
      title: 'Wnioski planistów',
      blocks: [
        {
          kind: 'text',
          text: 'Gdy planista nie może odpiąć pojazdu od obsadzonego zlecenia sam, składa wniosek z powodem. Na karcie zlecenia stoi wtedy baner „Wniosek o odpięcie czeka na decyzję”. Do Twojej decyzji pojazd zostaje przy zleceniu; nic nie wykona się samo, wniosek nie ma terminu.',
        },
        {
          kind: 'text',
          when: { on: ['unassignBeforeDeparture'] },
          text: 'Dopóki pojazd nie wyruszył na załadunek, planista odpina swój pojazd sam, z powodem. Wniosek do Ciebie powstaje dopiero po wyjeździe. Wyjazd system szacuje: początek okna załadunku minus dolot; status realizacji w TMS albo trwające okno załadunku to już po wyjeździe.',
        },
        {
          kind: 'task',
          title: 'Rozpatrzyć wniosek o odpięcie',
          steps: [
            'Otwórz zlecenie z kafelka „Wnioski o odpięcie” albo z powiadomienia.',
            'Przeczytaj na banerze, kto prosi, o jaki pojazd, dlaczego i do jakiego stanu wróci zlecenie.',
            'Kliknij „Akceptuję” albo „Odrzucam”.',
            'Przy akceptacji wpisz opcjonalny komentarz i kliknij „Odepnij pojazd”; przy odrzuceniu wpisz „Powód odrzucenia” i kliknij „Odrzuć wniosek”.',
          ],
          outcome: 'Po akceptacji pojazd schodzi ze zlecenia, a zlecenie wraca do planowania. Po odrzuceniu pojazd zostaje. Wnioskujący może wniosek wycofać, a wniosek, którego zlecenie zmieniło się inną drogą, zamyka się jako nieaktualny.',
        },
        {
          kind: 'task',
          title: 'Rozpatrzyć wniosek o zmianę pojazdu',
          when: { on: ['vehicleChangeRequest'] },
          intro: 'Po wyjeździe na załadunek planista nie zmienia pojazdu sam — proponuje inny swój pojazd z powodem.',
          steps: [
            'Kliknij kafelek „Wnioski o zmianę pojazdu i przekazanie” na pulpicie i otwórz zlecenie.',
            'Na banerze „Wniosek o zmianę pojazdu czeka na decyzję” sprawdź proponowany pojazd i powód.',
            'Kliknij „Akceptuję”, a potem „Zmień pojazd” — albo „Odrzucam” z powodem.',
          ],
          outcome: 'Akceptacja obsadza zlecenie proponowanym pojazdem tak jak „Zmień pojazd”, ze wszystkimi blokadami obsadzenia.',
        },
        {
          kind: 'image',
          when: { on: ['vehicleChangeRequest'] },
          image: { guide: 'vehicleChangeRequest', step: 2 },
          caption: 'Wnioski po wyjeździe pojazdu na pulpicie Team Leadera.',
        },
        {
          kind: 'task',
          title: 'Rozpatrzyć wniosek o przekazanie do Trade',
          when: { on: ['tradeTransferBeforeDeparture'] },
          steps: [
            'Otwórz zlecenie z kafelka „Wnioski o zmianę pojazdu i przekazanie”.',
            'Na banerze „Wniosek o przekazanie do Trade czeka na decyzję” przeczytaj powód.',
            'Kliknij „Akceptuję”, a potem „Przekaż do Trade” — albo „Odrzucam” z powodem.',
          ],
        },
      ],
    },
    {
      id: 'przekazanie-do-trade',
      title: 'Przekazanie obsadzonego zlecenia do Trade',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenie bez pojazdu każdy pracownik może przekazać do Trade. Zlecenie obsadzone pojazdem floty przekazuje Team Leader albo administrator (wyjątek dla planisty — niżej) — pojazd schodzi wtedy ze zlecenia. Szczegóły pracy w Trade opisuje rozdział „Trade”.',
        },
        {
          kind: 'task',
          title: 'Przekazać obsadzone zlecenie do Trade',
          steps: [
            'Otwórz kartę zlecenia w stanie „Obsadzone flotą”.',
            'W sekcji „Akcje” w polu „Zmień stan” wybierz „Przekazane do Trade”.',
            'Podaj powód przekazania, jeśli pole jest oznaczone jako wymagane.',
            'Kliknij „Zmień”.',
          ],
        },
        {
          kind: 'text',
          when: { off: ['tradeHandoverReasonOptional'] },
          text: 'Przy przekazaniu obsadzonego zlecenia powód jest wymagany.',
        },
        {
          kind: 'text',
          when: { on: ['tradeHandoverReasonOptional'] },
          text: 'Przy przekazaniu obsadzonego zlecenia powód jest opcjonalny — gdy go podasz, trafi do historii zmian.',
        },
        {
          kind: 'text',
          when: { on: ['tradeTransferBeforeDeparture'] },
          text: 'Planista sam przekazuje do Trade zlecenie obsadzone swoim pojazdem, dopóki pojazd nie wyruszył na załadunek. Po wyjeździe składa wniosek, który rozpatrujesz Ty.',
        },
      ],
    },
    {
      id: 'alerty',
      title: 'Alerty i przeplanowanie',
      blocks: [
        {
          kind: 'text',
          text: 'System co pewien czas sprawdza obsadzone zlecenia z załadunkiem w najbliższych 48 godzinach. Alert pojawia się, gdy obsadzony pojazd nie zdąży na załadunek albo nie dowiezie ładunku, gdy kierowca jest niedostępny, pojazd jest poza eksploatacją albo zajęty innym zleceniem, a także gdy wyraźnie bliżej stoi wolny pojazd. Baner na karcie zlecenia mówi wprost: „System niczego nie zmienił. O przeplanowaniu decydujesz Ty.”',
        },
        {
          kind: 'text',
          when: { on: ['replanAlertMasterOnly'] },
          text: 'Alert o przeplanowaniu widzą i przyjmują do wiadomości tylko osoby, które obsadzają: Team Leader i administratorzy. Planiści go nie widzą.',
        },
        {
          kind: 'text',
          when: { off: ['replanAlertMasterOnly'] },
          text: 'Alert o przeplanowaniu widzi każdy, kto planuje FTL — także planiści.',
        },
        {
          kind: 'task',
          title: 'Rozpatrzyć alert przeplanowania',
          steps: [
            'Otwórz zlecenie z kafelka „Alerty przeplanowania”, z dzwonka albo filtrem „Znaczniki” na liście zleceń.',
            'Przeczytaj powód alertu na banerze i kliknij „Pokaż rekomendowane pojazdy”.',
            'Jeśli trzeba zmienić pojazd — dodaj nowy wariant i kliknij „Zmień pojazd”.',
            'Kliknij „Przyjmuję do wiadomości” — wpis trafi do historii zlecenia, a ten sam alert nie wróci.',
          ],
        },
        {
          kind: 'task',
          title: 'Rozstrzygnąć podejrzenie anulacji z TMS',
          intro: 'Gdy w TMS zniknęło obsadzenie, system zgłasza podejrzenie anulacji do potwierdzenia.',
          steps: [
            'Otwórz zlecenie z banerem zmian z TMS.',
            'Sprawdź w TMS albo u klienta, czy zlecenie faktycznie anulowano.',
            'Kliknij „Potwierdzam anulację” (pojazd i warianty zostaną zwolnione, zlecenie przejdzie do „Nieaktualne”) albo „To nie anulacja” — w obu przypadkach z powodem.',
          ],
          outcome: 'Do TMS nic nie jest wysyłane — zapis zostaje wyłącznie w aplikacji.',
        },
        {
          kind: 'terms',
          title: 'Znaczniki wymagające Twojej uwagi',
          items: [
            { term: 'Straciły pojazd', meaning: 'Zlecenie było obsadzone, a straciło pojazd i nadal jest w planowaniu. Znika po ponownym obsadzeniu.' },
            { term: 'Jutro załadunek, bez auta', meaning: 'Alert z dnia poprzedzającego załadunek: zlecenie nadal nie ma wykonawcy.', when: { on: ['loadingTomorrowAlert'] } },
            { term: 'Rekomendowane przeplanowanie', meaning: 'Alert przeplanowania — podpowiedź, nie zmiana.' },
          ],
        },
      ],
    },
    {
      id: 'powiadomienia',
      title: 'Powiadomienia o wariantach i decyzjach',
      blocks: [
        {
          kind: 'text',
          text: 'Dzwonek w pasku pokazuje zmiany w działach, które obserwujesz — m.in. przekazanie zlecenia do Trade albo zwrot do FTL. Własne zmiany nie dają powiadomień i nic nie wychodzi pocztą.',
        },
        {
          kind: 'text',
          when: { on: ['ftlDecisionNotifications'] },
          text: 'Każdy nowy wariant dodany przez kogoś innego daje Ci powiadomienie „Nowy wariant do decyzji” z numerem pojazdu i autorem — dopóki wariant czeka na decyzję. Autorzy konkurencyjnych wariantów tego zlecenia też je dostają.',
        },
        {
          kind: 'image',
          when: { on: ['ftlDecisionNotifications'] },
          image: { guide: 'ftlDecisionNotifications', step: 3 },
          caption: 'Powiadomienie „Nowy wariant do decyzji” u Team Leadera.',
        },
        {
          kind: 'text',
          when: { on: ['variantNotifications'] },
          text: 'Planiści dostają powiadomienia imienne, gdy ich wariant zwolniono, gdy minął jego termin i gdy zlecenie obsadzono ich wariantem; wnioskujący — gdy rozpatrzyłeś wniosek. Pisz więc powody zwolnienia i odrzucenia tak, by planista zrozumiał decyzję. Na stronie powiadomień filtr „Tylko moje” zostawia powiadomienia imienne.',
        },
      ],
    },
  ],
};
